import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    EditButton,
    ShowButton,
    Filter,
    TextInput,
    SelectInput,
    ReferenceInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    usePermissions,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Typography, useMediaQuery, useTheme, Fab } from '@mui/material';
import {
    CheckCircle,
    Cancel,
    BatteryChargingFull,
    Error as ErrorIcon,
    Build,
    CloudOff,
    ChevronRight,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ChargersFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.chargers.list.filters.search')}
                source="search"
                alwaysOn
            />
            <ReferenceInput source="siteId" reference="sites">
                <SelectInput optionText="name" />
            </ReferenceInput>
            <SelectInput
                source="status"
                label={translate('resources.chargers.fields.status')}
                choices={[
                    { id: 'available', name: 'Online' },
                    { id: 'preparing', name: 'Preparing' },
                    { id: 'charging', name: 'Charging' },
                    { id: 'suspended_ev', name: 'Suspended EV' },
                    { id: 'suspended_evse', name: 'Suspended EVSE' },
                    { id: 'finishing', name: 'Finishing' },
                    { id: 'reserved', name: 'Reserved' },
                    { id: 'faulted', name: 'Faulted' },
                    { id: 'unavailable', name: 'Unavailable' },
                    { id: 'offline', name: 'Offline' },
                    { id: 'maintenance', name: 'Maintenance' },
                    { id: 'in_use', name: 'In Use' },
                ]}
            />
            <SelectInput
                source="ocppVersion"
                label={translate('resources.chargers.fields.ocppVersion')}
                choices={[
                    { id: '1.6', name: 'OCPP 1.6' },
                    { id: '2.0', name: 'OCPP 2.0' },
                    { id: '2.0.1', name: 'OCPP 2.0.1' },
                ]}
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'available':
            return {
                color: '#009A75',
                backgroundColor: '#E8F5E8',
                icon: CheckCircle,
            };
        case 'charging':
            return {
                color: '#2196F3',
                backgroundColor: '#E3F2FD',
                icon: BatteryChargingFull,
            };
        case 'error':
            return {
                color: '#F44336',
                backgroundColor: '#FFEBEE',
                icon: ErrorIcon,
            };
        case 'maintenance':
            return {
                color: '#FF9800',
                backgroundColor: '#FFF3E0',
                icon: Build,
            };
        case 'offline':
            return {
                color: '#9E9E9E',
                backgroundColor: '#F5F5F5',
                icon: CloudOff,
            };
        case 'unavailable':
            return {
                color: '#757575',
                backgroundColor: '#EEEEEE',
                icon: Cancel,
            };
        case 'faulted':
            return {
                color: '#D32F2F',
                backgroundColor: '#FFCDD2',
                icon: ErrorIcon,
            };
        case 'suspended':
            return {
                color: '#F57C00',
                backgroundColor: '#FFE0B2',
                icon: Cancel,
            };
        case 'finishing':
            return {
                color: '#388E3C',
                backgroundColor: '#E8F5E9',
                icon: BatteryChargingFull,
            };
        case 'reserved':
            return {
                color: '#7B1FA2',
                backgroundColor: '#F3E5F5',
                icon: CheckCircle,
            };
        case 'preparing':
            return {
                color: '#0288D1',
                backgroundColor: '#E1F5FE',
                icon: BatteryChargingFull,
            };
        default:
            return {
                color: '#9E9E9E',
                backgroundColor: '#F5F5F5',
                icon: Cancel,
            };
    }
};

const StatusField = ({ record }: { record: any }) => {
    const translate = useTranslate();
    const config = getStatusConfig(record?.status);
    const StatusIcon = config.icon;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
                icon={<StatusIcon sx={{ fontSize: 16 }} />}
                label={translate(`resources.chargers.statuses.${record?.status?.toLowerCase()}`)}
                size="small"
                sx={{
                    backgroundColor: config.backgroundColor,
                    color: config.color,
                    fontWeight: 500,
                    maxWidth: '100%',
                    '& .MuiChip-icon': {
                        color: config.color,
                    },
                    '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    },
                }}
            />
            {record?.isManualOverride && (
                <Chip
                    icon={<Build sx={{ fontSize: 14 }} />}
                    label="Manual"
                    size="small"
                    sx={{
                        backgroundColor: '#FFF3E0',
                        color: '#FF9800',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                        height: '20px',
                        '& .MuiChip-icon': {
                            color: '#FF9800',
                            marginLeft: '4px',
                        },
                        '& .MuiChip-label': {
                            padding: '0 6px',
                        },
                    }}
                />
            )}
        </Box>
    );
};

const PowerLevelField = ({ record }: { record: any }) => {
    const power = record?.power || 0;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <BatteryChargingFull sx={{ fontSize: 16, color: '#666' }} />
            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {power}kW
            </span>
        </Box>
    );
};

// Normalize OCPP connector status (e.g. "SuspendedEVSE") to i18n key
// (e.g. "suspended_evse"). Mirrors the same helper in ChargersShow.tsx.
const getConnectorStatusKey = (status: string): string => {
    if (!status) return '';
    const s = status.toLowerCase();
    if (s === 'suspendedevse') return 'suspended_evse';
    if (s === 'suspendedev') return 'suspended_ev';
    return s;
};

const getConnectorStatusColor = (status: string): { bg: string; text: string } => {
    const key = getConnectorStatusKey(status);
    if (key === 'available') return { bg: '#E8F5E8', text: '#00C853' };
    if (key === 'charging' || key === 'preparing' || key === 'finishing') return { bg: '#E3F2FD', text: '#2196F3' };
    if (key === 'faulted' || key === 'offline') return { bg: '#FFEBEE', text: '#F44336' };
    if (key === 'suspended_ev' || key === 'suspended_evse' || key === 'unavailable') return { bg: '#FFF3E0', text: '#FF9800' };
    if (key === 'reserved') return { bg: '#F3E5F5', text: '#7B1FA2' };
    return { bg: '#F5F5F5', text: '#757575' };
};

const ConnectorsField = ({ record, dense = false }: { record: any; dense?: boolean }) => {
    const translate = useTranslate();
    const connectors: Array<{ connectorId: number; status: string }> = record?.connectors || [];

    if (connectors.length === 0) {
        return <Typography variant="caption" sx={{ color: '#9E9E9E' }}>—</Typography>;
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {connectors.map((c) => {
                const colors = getConnectorStatusColor(c.status);
                const label = translate(
                    `resources.chargers.connectorStatuses.${getConnectorStatusKey(c.status)}`,
                    { _: c.status || '-' }
                );
                return (
                    <Chip
                        key={c.connectorId}
                        label={`#${c.connectorId} · ${label}`}
                        size="small"
                        component="div"
                        clickable={false}
                        sx={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                            fontWeight: 500,
                            fontSize: dense ? '0.65rem' : '0.7rem',
                            height: dense ? 18 : 22,
                            '& .MuiChip-label': { padding: dense ? '0 6px' : '0 8px' },
                            '&:hover': { backgroundColor: colors.bg },
                        }}
                    />
                );
            })}
        </Box>
    );
};

const MobileChargersList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();

    if (isLoading) return null;
    if (!data || data.length === 0) return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" sx={{ color: '#606060' }}>
                {translate('ra.navigation.no_results')}
            </Typography>
        </Box>
    );

    return (
        <Box>
            {data.map((record: any) => {
                const config = getStatusConfig(record?.status);
                const StatusIcon = config.icon;

                return (
                    <RecordContextProvider key={record.id} value={record}>
                        <Box
                            onClick={() => navigate(`/chargers/${record.id}/show`)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                py: 1.5,
                                px: 2,
                                borderBottom: '1px solid #F0F0F0',
                                cursor: 'pointer',
                                '&:active': { backgroundColor: '#F5F5F5' },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography sx={{
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                        minWidth: 0,
                                    }}>
                                        {record.name}
                                    </Typography>
                                    <Chip
                                        icon={<StatusIcon sx={{ fontSize: 14 }} />}
                                        label={translate(`resources.chargers.statuses.${record?.status?.toLowerCase()}`)}
                                        size="small"
                                        component="div"
                                        clickable={false}
                                        sx={{
                                            backgroundColor: config.backgroundColor,
                                            color: config.color,
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            height: 22,
                                            flexShrink: 0,
                                            '& .MuiChip-icon': {
                                                color: config.color,
                                            },
                                            '&:hover': { backgroundColor: config.backgroundColor },
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {record.model && (
                                        <Typography sx={{ color: '#606060', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {record.model}
                                        </Typography>
                                    )}
                                    {record.siteName && (
                                        <Typography sx={{ color: '#909090', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {record.siteName}
                                        </Typography>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BatteryChargingFull sx={{ fontSize: 14, color: '#909090' }} />
                                        <Typography sx={{ color: '#606060', fontSize: '0.7rem', fontWeight: 600 }}>
                                            {record.power || 0}kW
                                        </Typography>
                                    </Box>
                                    {record.isManualOverride && (
                                        <Chip
                                            icon={<Build sx={{ fontSize: 12 }} />}
                                            label="Manual"
                                            size="small"
                                            component="div"
                                            clickable={false}
                                            sx={{
                                                backgroundColor: '#FFF3E0',
                                                color: '#FF9800',
                                                fontWeight: 500,
                                                fontSize: '0.65rem',
                                                height: 18,
                                                '& .MuiChip-icon': {
                                                    color: '#FF9800',
                                                    marginLeft: '2px',
                                                },
                                                '& .MuiChip-label': {
                                                    padding: '0 4px',
                                                },
                                            }}
                                        />
                                    )}
                                </Box>
                                {(record.connectors || []).length > 0 && (
                                    <Box sx={{ mt: 0.5 }}>
                                        <ConnectorsField record={record} dense />
                                    </Box>
                                )}
                            </Box>
                            <ChevronRight sx={{ color: '#BDBDBD', fontSize: 20, flexShrink: 0 }} />
                        </Box>
                    </RecordContextProvider>
                );
            })}
        </Box>
    );
};

export const ChargersList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const canEdit = permissions?.includes('chargers:write:all');

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<ChargersFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                perPage={25}
                sort={{ field: 'createdAt', order: 'DESC' }}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': {
                            margin: 0,
                        },
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': {
                            display: 'none',
                        },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiInputBase-root': {
                            width: '100% !important',
                        },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileChargersList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 600,
                            },
                            '& .RaDatagrid-row': {
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField source="name" />

                        <TextField source="model" />

                        <FunctionField
                            source="status"
                            label={translate('resources.chargers.fields.status')}
                            render={(record: any) => <StatusField record={record} />}
                        />

                        <FunctionField
                            source="connectors"
                            label={translate('resources.chargers.fields.connectors')}
                            render={(record: any) => <ConnectorsField record={record} />}
                            sortable={false}
                        />

                        <FunctionField
                            source="power"
                            label={translate('resources.chargers.fields.power')}
                            render={(record: any) => <PowerLevelField record={record} />}
                        />

                        <TextField
                            source="siteName"
                            label={translate('resources.chargers.fields.site')}
                        />

                        <TextField
                            source="serialNumber"
                            label={translate('resources.chargers.fields.serialNumber')}
                        />

                        <TextField
                            source="ocppVersion"
                            label={translate('resources.chargers.fields.ocppVersion')}
                        />

                        <TextField
                            source="firmwareVersion"
                            label={translate('resources.chargers.fields.firmwareVersion')}
                        />

                        <DateField
                            source="createdAt"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                            }}
                        />

                        <ShowButton label="" />
                        {canEdit && <EditButton label="" />}
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/chargers/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
